import { STANDARD_EASING, STANDARD_TIME } from 'constants/animations';
import { MouseEvent } from 'react';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { BaseButton } from 'ui/button/Styled';
import { Pin16 } from 'ui/icons/aurora/16px/Pin16';
import { Unpin16 } from 'ui/icons/aurora/16px/Unpin16';
import { body2, borderRadius, colors, spacing } from 'ui/theme';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { noop } from 'utils/noop';

/**
 * Project Option
 */
const ProjectRow = styled.div`
  display: flex;
  padding: 0;
  color: ${colors.textPrimary};
  border-radius: ${borderRadius.md};

  &:hover {
    background-color: ${colors.backgroundSecondary};
  }

  &[aria-current='true'] {
    color: ${colors.textInteractive};
    background-color: ${colors.backgroundInteractive};

    &:hover {
      color: ${colors.textInteractiveHover};
    }
  }

  &[aria-selected='true'] {
    background-color: ${colors.backgroundSecondary};
  }

  &:focus-within {
    outline: 2px solid ${colors.linkFocusOutline};
    background-color: ${colors.backgroundSecondary};
  }
`;

const StyledButton = styled(BaseButton)`
  ${body2}
  justify-content: flex-start;
  height: auto;
  color: inherit;
  padding: 0;
  text-align: initial;
  background-color: transparent;
  border: none;
  border-radius: 0;
  transition:
    color,
    background-color ${STANDARD_TIME}s ${STANDARD_EASING};

  &[data-type='title'] {
    flex-grow: 1;
  }

  &[data-type='action'] {
    min-width: ${spacing.doubleQuarter};
    visibility: hidden;

    ${ProjectRow}:hover & {
      visibility: visible;
    }
  }

  &:first-child {
    border-radius: ${borderRadius.md} 0 0 ${borderRadius.md};
  }

  &:last-child {
    border-radius: 0 ${borderRadius.md} ${borderRadius.md} 0;
  }

  &:focus {
    outline: none;

    &::before {
      content: none;
    }
  }

  &:hover {
    color: inherit;
    background-color: transparent;
  }
`;

const Content = styled.span`
  flex-grow: 1;
  display: flex;
  align-items: center;
  padding: ${spacing.threeEights} ${spacing.half};
`;

const StyledTooltip = styled(Tooltip)`
  display: flex;
  align-self: stretch;
  align-items: center;
  width: 100%;
  min-height: 100%;
`;

type ProjectOptionProps = {
  id: number;
  isCurrentPage: boolean;
  isPinned?: boolean;
  isSelected: boolean;
  onActionClick?: (e: MouseEvent) => void;
  onProjectClick: (e: MouseEvent) => void;
  title: string;
};

export const ProjectOption = ({
  id,
  isCurrentPage,
  isPinned = false,
  isSelected,
  onActionClick = noop,
  onProjectClick,
  title,
}: ProjectOptionProps) => {
  const pinnedText = isPinned ? 'pinned' : 'unpinned';
  return (
    <ProjectRow aria-current={isCurrentPage} aria-selected={isSelected} data-test={`project-${id}`}>
      <StyledButton
        data-test={`project-option-${pinnedText}`}
        data-type="title"
        onClick={onProjectClick}
        size={BUTTON_SIZES.MEDIUM}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <Content>{title}</Content>
      </StyledButton>
      <StyledButton
        data-test={`project-action-${pinnedText}`}
        data-type="action"
        data-state={pinnedText}
        onClick={onActionClick}
        tabIndex={-1}
        size={BUTTON_SIZES.MEDIUM}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <StyledTooltip
          placement={TipPlacement.Left}
          delayDuration={500}
          isEnabled
          isUsedOnButton
          tooltipContent={isPinned ? 'Unpin project' : 'Pin to top'}
        >
          <Content>{isPinned ? <Unpin16 isColorInherited /> : <Pin16 isColorInherited />}</Content>
        </StyledTooltip>
      </StyledButton>
    </ProjectRow>
  );
};
